import { Message } from "../types";
import { proposalFromStored } from "./gbpProposalText";
import { blogToChatMarkdown } from "../../../../features/wordpress/platforms/wordpress/utils/blogContent";

type ConversationHistoryMessage = {
  /** Assigned by fetchConversationMessages (`msg_<thread>_<index>`). Carried onto
   *  the Message so a restored turn is addressable — a card-only turn has no prose
   *  to tell it apart, and the thread would otherwise key it by array position. */
  id?: string;
  role: "user" | "assistant";
  content?: string;
  type?: string;
  action_name?: string;
  image_urls?: string[];
  research_started_at?: number;
  image_batch_started_at?: number;
  research_document?: {
    title?: string;
    fmt?: string;
    url?: string | null;
    key?: string | null;
    formats?: Record<string, { url?: string | null; key?: string | null }>;
    preview?: string;
    citations?: string[];
    char_count?: number;
    partial?: boolean;
  };
  posts?: Array<{
    platform?: string;
    title?: string;
    post?: string;
    content?: string;
    image_url?: string;
    hashtags?: string[];
    call_to_action?: string;
    scheduled_time?: string;
  }>;
  blog?: {
    id?: string;
    wordpressUrl?: string;
    title?: string;
    excerpt?: string;
    content?: string;
    author?: string;
    featuredImage?: string;
    author_bio?: string;
    author_linkedin?: string;
    tags?: string[];
    category?: string;
    published?: boolean;
  };
  outreach_email?: {
    subject?: string;
    body?: string;
    cta_style?: string;
    merge_fields?: string[];
    booking_link?: string;
    campaign_context?: string;
    notes?: string;
  };
  calls?: {
    calls?: Array<{
      contact_id?: string | null;
      contact_name?: string;
      phone?: string;
      direction?: string;
      status?: string;
      missed?: boolean;
      date?: string | null;
      duration_seconds?: number | null;
      conversation_id?: string | null;
    }>;
    truncated?: boolean;
  };
  card_contacts?: {
    contacts?: Array<{
      name?: string | null;
      title?: string | null;
      company?: string | null;
      email?: string | null;
      phone?: string | null;
      mobile?: string | null;
      website?: string | null;
      draft?: { subject?: string; body?: string } | null;
      sendable?: boolean;
      reason?: string | null;
      saved?: boolean;
      sent?: boolean;
    }>;
    capture_context?: string;
    tags?: string[];
    proposed_action?: string;
    saved?: boolean;
    sent?: boolean;
  };
  card_details?: {
    contacts?: Array<{
      name?: string | null;
      title?: string | null;
      company?: string | null;
      email?: string | null;
      phone?: string | null;
      mobile?: string | null;
      website?: string | null;
    }>;
    capture_context?: string;
  };
  /** SGL-328: the Business Profile card as stored (storableProposal + summary). */
  gbp_proposal?: Record<string, unknown>;
  /** The row's DynamoDB sort key, carried through by the history fetch. */
  sk?: string;
};

/**
 * Overlay a blog's post-generation state from the per-thread localStorage cache onto
 * the server-loaded history.
 *
 * The server now persists blogs in position, so it owns each blog's order and content.
 * But a blog's later state — Saved `id`, Published `wordpressUrl`/`published` — is only
 * ever recorded in the cache (it is never written back to the server row). So for each
 * server blog, copy that state from the matching cached blog. Matching is by title+content,
 * which are fixed at generation and never change on the inline message, so the key is
 * stable across save/publish/edit. Cached blogs with no server counterpart (legacy threads
 * created before blogs were persisted, or a blog that failed to persist) are appended, so
 * a blog is never dropped on reopen — preserving the prior behaviour for those threads.
 *
 * Pure: `server` is not mutated.
 */
export function overlayCachedBlogState(server: Message[], cached: Message[]): Message[] {
  const cachedBlogs = (cached || []).filter((m) => m?.blogPost);
  if (cachedBlogs.length === 0) return server;

  // JSON-encode each part so the title/content boundary is unambiguous — a plain
  // separator could collide (e.g. title "A B" + content "C" vs "A" + "B C").
  const sig = (b?: Message["blogPost"]) =>
    `${JSON.stringify(b?.title ?? "")}:${JSON.stringify(b?.content ?? "")}`;
  // Group cached blogs by signature so state is consumed one-to-one, in order, when
  // several blogs happen to share a signature (e.g. an identical regeneration).
  const bySig = new Map<string, Message[]>();
  for (const m of cachedBlogs) {
    const list = bySig.get(sig(m.blogPost));
    if (list) list.push(m);
    else bySig.set(sig(m.blogPost), [m]);
  }

  const merged = server.map((m) => {
    if (!m.blogPost) return m;
    const match = bySig.get(sig(m.blogPost))?.shift();
    if (!match?.blogPost) return m;
    return {
      ...m,
      blogPost: {
        ...m.blogPost,
        id: match.blogPost.id ?? m.blogPost.id,
        wordpressUrl: match.blogPost.wordpressUrl ?? m.blogPost.wordpressUrl,
        published: match.blogPost.published ?? m.blogPost.published,
      },
    };
  });

  // Whatever cached blogs were not consumed above have no server counterpart — append
  // them (in their original order) so legacy-thread blogs still appear.
  const leftover = cachedBlogs.filter((m) => bySig.get(sig(m.blogPost))?.includes(m));
  return leftover.length > 0 ? [...merged, ...leftover] : merged;
}

/**
 * Overlay an outreach email draft's local edits and Approved state from the per-thread
 * localStorage cache onto the server-loaded history.
 *
 * There is no backend write for edits or Approve (SGL-83 — UI state only), so unlike
 * the blog overlay above, the cache is the ONLY place this state ever lives; the
 * server row always holds the draft exactly as the model first generated it. Matching
 * is by the server draft's original subject+body signature so a match survives edits
 * (the cached copy's subject/body may differ from the server's after an edit — that's
 * the whole point of overlaying it).
 *
 * Pure: `server` is not mutated.
 */
export function overlayCachedOutreachEmailState(server: Message[], cached: Message[]): Message[] {
  const cachedDrafts = (cached || []).filter((m) => m?.outreachEmail);
  if (cachedDrafts.length === 0) return server;

  // Cached copies store their own edited subject/body, so they cannot be matched by
  // content — they are consumed in original document order instead, one per server
  // outreach-email message. This is safe because the cache write (NewChatWindow) only
  // ever writes the current in-order snapshot of outreach-email-bearing messages.
  const queue = [...cachedDrafts];

  const merged = server.map((m) => {
    if (!m.outreachEmail) return m;
    const match = queue.shift();
    if (!match?.outreachEmail) return m;
    return { ...m, outreachEmail: { ...match.outreachEmail } };
  });

  // If the cache has more outreach-email messages than the server does (the server
  // write in save_message failed for one, but the local UI state and cache write
  // never depend on that write succeeding), append what's left over rather than
  // silently dropping a draft the user may have already edited or approved.
  return queue.length > 0 ? [...merged, ...queue] : merged;
}

export function mapConversationHistoryToMessages(
  history: ConversationHistoryMessage[],
  /** The thread these rows came from. A restored scanned-cards turn needs it (with
   *  the row's own `sk`) to name itself when the owner approves, so a card reopened
   *  and then saved still gets marked settled. */
  threadId?: string | null
): Message[] {
  return history.map((msg) => {
    const message: Message = {
      role: msg.role,
      content: msg.content ?? "",
    };
    if (msg.id) message.id = msg.id;

    if (msg.type === "action" || msg.type === "action_result") {
      message.type = msg.type;
    }
    if (msg.action_name != null) message.action_name = msg.action_name;
    if (Array.isArray(msg.image_urls)) message.image_urls = msg.image_urls;

    if (msg.type === "action_result" && Array.isArray(msg.posts) && msg.posts.length > 0) {
      message.generatedPosts = {
        posts: msg.posts.map((p, i) => ({
          temp_id: `post_${i}`,
          platform: p.platform || "",
          title: p.title || "",
          content: p.post ?? p.content ?? "",
          image_url:
            typeof p.image_url === "string" && p.image_url.startsWith("http")
              ? p.image_url
              : "",
          hashtags: Array.isArray(p.hashtags) ? p.hashtags : [],
          call_to_action: p.call_to_action ?? "",
          scheduled_time: typeof p.scheduled_time === "string" ? p.scheduled_time : "",
        })),
      };
    }

    // Blog delivered via the output_blog tool. Reconstruct the same assistant
    // message the live stream produces: the blog card (blogPost) plus the inline
    // Markdown body, so a reopened thread renders the blog in its original position.
    if (msg.type === "blog" && msg.blog) {
      const b = msg.blog;
      message.blogPost = {
        id: b.id,
        wordpressUrl: b.wordpressUrl,
        title: b.title ?? "",
        excerpt: b.excerpt ?? "",
        content: b.content ?? "",
        author: b.author ?? "",
        featuredImage: b.featuredImage ?? "",
        author_bio: b.author_bio ?? "",
        author_linkedin: b.author_linkedin ?? "",
        tags: Array.isArray(b.tags) ? b.tags : [],
        category: b.category ?? "",
        published: b.published ?? false,
      };
      message.content = message.content || blogToChatMarkdown(b);
    }

    // Outreach email draft delivered via the output_outreach_email tool. The server
    // row always holds the draft as first generated — any local edit or Approve is
    // overlaid separately from the per-thread cache (see overlayCachedOutreachEmailState).
    if (msg.type === "outreach_email" && msg.outreach_email) {
      const oe = msg.outreach_email;
      const ctaStyle = oe.cta_style === "booking_link" || oe.cta_style === "reply_only"
        ? oe.cta_style
        : "hybrid";
      message.outreachEmail = {
        subject: oe.subject ?? "",
        body: oe.body ?? "",
        cta_style: ctaStyle,
        merge_fields: Array.isArray(oe.merge_fields) ? oe.merge_fields : [],
        booking_link: oe.booking_link ?? "",
        campaign_context: oe.campaign_context ?? "",
        notes: oe.notes ?? "",
      };
      message.content = message.content || "Here’s your outreach email draft ✉️";
    }

    // GHL calls list delivered via the output_calls tool — read-only, no local
    // edit/overlay needed (unlike outreach_email's draft state).
    if (msg.type === "calls" && msg.calls && Array.isArray(msg.calls.calls)) {
      message.calls = {
        calls: msg.calls.calls.map((c) => ({
          contact_id: c.contact_id ?? null,
          contact_name: c.contact_name ?? "",
          phone: c.phone ?? "",
          direction: c.direction === "inbound" || c.direction === "outbound" ? c.direction : "unknown",
          status: c.status ?? "unknown",
          missed: !!c.missed,
          date: c.date ?? null,
          duration_seconds: typeof c.duration_seconds === "number" ? c.duration_seconds : null,
          conversation_id: c.conversation_id ?? null,
        })),
        truncated: !!msg.calls.truncated,
      };
    }

    // A scan the owner had not finished correcting when they closed the tab. Comes
    // back as the same correction card: re-scanning costs them a second read, and
    // the drafting step has not run yet so there is nothing else to show.
    if (msg.type === "card_details" && msg.card_details && Array.isArray(msg.card_details.contacts)) {
      const cd = msg.card_details;
      message.cardDetails = {
        contacts: (cd.contacts ?? []).map((c) => ({
          name: c.name ?? null,
          title: c.title ?? null,
          company: c.company ?? null,
          email: c.email ?? null,
          phone: c.phone ?? null,
          mobile: c.mobile ?? null,
          website: c.website ?? null,
        })),
        captureContext: cd.capture_context ?? "",
        brief: "",
        // Without the pair, a correction typed after a reload appends a second scan of
        // the same person rather than rewriting this row.
        threadId: threadId ?? null,
        cardMessageSk: msg.sk ?? null,
      };
    }

    // The Visibility Manager's Business Profile card (SGL-328), as the owner left
    // it: typed values, the Google auto-update answer, or dismissed. Carries its
    // row so a further change rewrites it instead of adding a second card.
    if (msg.type === "gbp_proposal" && msg.gbp_proposal) {
      const proposal = proposalFromStored(msg.gbp_proposal, threadId ?? null, msg.sk ?? null);
      if (proposal) message.gbpProposal = proposal;
    }

    // Scanned cards + drafts (output_card_contacts, SGL-346). Restored read-only:
    // a reload mid-review must show the same cards and the same draft text rather
    // than force a re-scan the customer would be charged for again.
    if (msg.type === "card_contacts" && msg.card_contacts && Array.isArray(msg.card_contacts.contacts)) {
      const cc = msg.card_contacts;
      message.cardContacts = {
        contacts: (cc.contacts ?? []).map((c) => {
          const d = c.draft;
          return {
            name: c.name ?? null,
            title: c.title ?? null,
            company: c.company ?? null,
            email: c.email ?? null,
            phone: c.phone ?? null,
            mobile: c.mobile ?? null,
            website: c.website ?? null,
            draft:
              d && typeof d.subject === "string" && typeof d.body === "string"
                ? { subject: d.subject, body: d.body }
                : null,
            sendable: c.sendable === true,
            reason: c.reason || null,
            saved: c.saved === true,
            sent: c.sent === true,
          };
        }),
        captureContext: cc.capture_context ?? "",
        tags: Array.isArray(cc.tags) ? cc.tags.filter((t) => typeof t === "string" && t.trim()) : [],
        proposedAction:
          cc.proposed_action === "save_only" || cc.proposed_action === "draft_only"
            ? cc.proposed_action
            : "save_and_send",
        // Restoring these is what stops a settled card coming back claiming nothing
        // was sent, with the send button live for people who were already emailed.
        saved: cc.saved === true,
        sent: cc.sent === true,
        threadId: threadId ?? null,
        cardMessageSk: typeof msg.sk === "string" ? msg.sk : null,
      };
    }

    if (msg.type === "image_result" && Array.isArray(msg.image_urls) && msg.image_urls.length > 0) {
      message.generatedImages = {
        prompt: "",
        provider: "",
        urls: msg.image_urls,
      };
      message.content = message.content || "Here are your generated images! 🎨";
    }

    // Deep-research still running (synthetic message from the backend when the thread
    // has an in-progress research). Renders the animated loading card on reload.
    if (msg.type === "research_pending") {
      message.type = "research_pending";
      if (typeof msg.research_started_at === "number") {
        message.researchStartedAt = msg.research_started_at;
      }
    }

    // Bulk image batch still running (synthetic message from the backend when the
    // thread has an in-progress generate_image_batch). Renders the animated loading
    // card on reload/navigate-back instead of nothing.
    if (msg.type === "image_batch_pending") {
      message.type = "image_batch_pending";
      if (typeof msg.image_batch_started_at === "number") {
        message.imageBatchStartedAt = msg.image_batch_started_at;
      }
    }

    // Deep-research report card. The backend re-signs `url` from the stored S3 key
    // on load, so the download link is always fresh.
    if (msg.type === "research_document" && msg.research_document) {
      const doc = msg.research_document;
      message.type = "research_document";
      message.researchDocument = {
        title: doc.title ?? "",
        fmt: doc.fmt ?? "md",
        url: doc.url ?? null,
        formats: doc.formats && typeof doc.formats === "object" ? doc.formats : undefined,
        preview: doc.preview ?? "",
        citations: Array.isArray(doc.citations) ? doc.citations : [],
        char_count: typeof doc.char_count === "number" ? doc.char_count : undefined,
        partial: doc.partial ?? false,
      };
    }

    return message;
  });
}
